import React, { useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { MODULE_CATALOG, type ModuleField } from '../services/teamPreferences.service';
import { geoService } from '../services/geo.service';
import {
  useAdminOverview,
  useAdminTeamMetrics,
  useUpdateTeamPrefs,
  useUpdateUserPrefs,
  type AdminTeamMetrics,
} from '../hooks/useAdminOverview';
import type { AdminTeam, AdminUser } from '../services/backend/appwrite/adminPrefsService';
import {
  AccessStatusSelect,
  ADMIN_ACCESS_STATUS,
  CreditsBarChart,
  ImpersonateButton,
  StatusChips,
  ToggleSwitch,
  normalizeAdminAccessStatus,
  type AdminAccessStatus,
  type BarDatum,
} from './admin/atoms/AdminPrimitives';
import BrainiacsLoader from './atoms/BrainiacsLoader';
import TeamEditorDrawer from './admin/teams/TeamEditorDrawer';
import { ReportTemplateEditor } from './admin/ReportTemplateEditor';
import { SellerReportTemplate } from './admin/SellerReportTemplate';
import { CampaignTemplateEditor } from './admin/CampaignTemplateEditor';
import UserEditorDrawer from './admin/teams/UserEditorDrawer';

interface AdminDashboardProps {
  onClientSelect?: (clientEmail: string) => void;
}

type DirectoryView = 'teams' | 'people';
type StatusFilter = 'all' | AdminAccessStatus;

const MODULE_SHORT_LABELS: Record<ModuleField, string> = {
  leads: 'Leads',
  outreach: 'SDR',
  sales: 'Sales',
  workers: 'Workers',
  nexus: 'Nexus',
  chat: 'Chat',
  goals: 'Goals',
};

const parseBool = (value: unknown): boolean =>
  value === true || (typeof value === 'string' && value.toLowerCase() === 'true');

const isStoredValue = (value: unknown): value is string =>
  typeof value === 'string' && value.trim() !== '' && value.trim().toLowerCase() !== 'null';

const isInternalTeam = (team: AdminTeam): boolean =>
  String(team.prefs?.lvl1_id || '').trim() === '11135' || /brainiac/i.test(team.name || '');

const getInitials = (name?: string | null): string => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length > 1) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return (parts[0]?.slice(0, 2) || '?').toUpperCase();
};

const formatDate = (value?: string | null): string => {
  if (!value) return 'Date unavailable';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Date unavailable';
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
};

const statusOfTeam = (team: AdminTeam): AdminAccessStatus =>
  normalizeAdminAccessStatus(team.prefs?.status);

const statusOfUser = (user: AdminUser): AdminAccessStatus => {
  if (user.is_invitation || user.membership_status === 'pending') return 'invited';
  if (user.account_enabled === false) return 'inactive';
  return normalizeAdminAccessStatus(user.prefs?.status ?? user.status);
};

const Avatar: React.FC<{
  name: string;
  imageUrl?: unknown;
  size?: 'sm' | 'md' | 'lg';
  square?: boolean;
  contain?: boolean;
}> = ({ name, imageUrl, size = 'md', square = false, contain = false }) => {
  const sizeClass = size === 'sm' ? 'h-7 w-7 text-[9px]' : size === 'lg' ? 'h-14 w-14 text-sm' : 'h-11 w-11 text-[11px]';
  const radius = square ? 'rounded-[14px]' : 'rounded-full';
  return (
    <span className={`relative flex shrink-0 items-center justify-center overflow-hidden border border-white/10 bg-[#15171d] font-black tracking-tight text-white/70 ${sizeClass} ${radius}`}>
      {isStoredValue(imageUrl) ? (
        <img src={imageUrl} alt="" className={`h-full w-full ${contain ? 'object-contain p-1.5' : 'object-cover'}`} />
      ) : (
        getInitials(name)
      )}
    </span>
  );
};

const ModuleStrip: React.FC<{ team: AdminTeam }> = ({ team }) => {
  const activeCount = MODULE_CATALOG.reduce(
    (total, module) => total + (parseBool(team.prefs?.[module.prefKey]) ? 1 : 0),
    0,
  );

  return (
    <div className="min-w-0">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-[10px] font-bold text-white/42">
          <b className="text-white/85">{activeCount}</b> of {MODULE_CATALOG.length} live
        </span>
        <span className="h-px flex-1 bg-white/[0.05]" />
      </div>
      <div className="flex flex-wrap gap-1.5">
        {MODULE_CATALOG.map((module) => {
          const active = parseBool(team.prefs?.[module.prefKey]);
          return (
            <span
              key={module.field}
              title={`${module.label}: ${active ? 'active' : 'inactive'}`}
              className={`inline-flex h-7 items-center gap-1.5 rounded-lg border px-2 text-[9px] font-black uppercase tracking-[0.08em] transition-all ${
                active
                  ? 'border-[#7180FF]/40 bg-[#5260FF]/20 text-[#c4caff] shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]'
                  : 'border-white/[0.10] bg-white/[0.035] text-white/42'
              }`}
            >
              <Icon icon={module.icon} className={`h-3.5 w-3.5 ${active ? 'text-[#939dff]' : 'text-white/32'}`} />
              {MODULE_SHORT_LABELS[module.field]}
            </span>
          );
        })}
      </div>
    </div>
  );
};

const MemberStack: React.FC<{ members: AdminUser[] }> = ({ members }) => {
  const accepted = members.filter((member) => !member.is_invitation && member.membership_status !== 'pending');
  const invited = members.length - accepted.length;
  return (
    <div>
      <div className="flex items-center">
        {accepted.slice(0, 4).map((member, index) => (
          <span key={member.$id} className={index === 0 ? '' : '-ml-2'}>
            <Avatar name={member.name || member.email} imageUrl={member.prefs?.profilePictureUrl} size="sm" />
          </span>
        ))}
        {accepted.length > 4 && (
          <span className="-ml-2 flex h-7 w-7 items-center justify-center rounded-full border border-[#0b0c0f] bg-white/10 text-[8px] font-black text-white/65">
            +{accepted.length - 4}
          </span>
        )}
      </div>
      <p className="mt-2 text-[10px] font-semibold text-white/42">
        <b className="text-white/80">{accepted.length}</b> member{accepted.length === 1 ? '' : 's'}
        {invited > 0 ? <span className="text-sky-300/75"> · {invited} invited</span> : null}
      </p>
    </div>
  );
};

interface TeamDirectoryRowProps {
  team: AdminTeam;
  members: AdminUser[];
  metrics?: AdminTeamMetrics | null;
  metricsLoading: boolean;
  status: AdminAccessStatus;
  statusBusy: boolean;
  countryLabel: string;
  isExpanded: boolean;
  userStatusOverrides: Record<string, AdminAccessStatus>;
  pendingStatusKey: string | null;
  onToggle: () => void;
  onOpenDetails: () => void;
  onOpenUser: (userId: string) => void;
  onImpersonate?: (user: AdminUser) => void;
  onStatusChange: (status: AdminAccessStatus) => void;
  onUserStatusChange: (user: AdminUser, status: AdminAccessStatus) => void;
}

const TeamDirectoryRow: React.FC<TeamDirectoryRowProps> = ({
  team,
  members,
  metrics,
  metricsLoading,
  status,
  statusBusy,
  countryLabel,
  isExpanded,
  userStatusOverrides,
  pendingStatusKey,
  onToggle,
  onOpenDetails,
  onOpenUser,
  onImpersonate,
  onStatusChange,
  onUserStatusChange,
}) => {
  const brandColour = isStoredValue(team.prefs?.brand_primary_colour) && /^#[0-9a-f]{6}$/i.test(team.prefs.brand_primary_colour)
    ? team.prefs.brand_primary_colour
    : '#5260FF';
  const companyMeta = [countryLabel, isStoredValue(team.prefs?.currency) ? team.prefs.currency : null]
    .filter(Boolean)
    .join(' · ');
  const campaigns = metrics?.campaigns_by_status;

  return (
    <section className={`overflow-hidden rounded-[24px] border bg-[#111319] shadow-[0_16px_50px_rgba(0,0,0,0.24)] transition-all duration-300 ${isExpanded ? 'border-[#6673ff]/30 ring-1 ring-[#5260FF]/10' : 'border-white/[0.11] hover:border-white/[0.17]'}`}>
      <article
        onClick={onToggle}
        className="group relative grid cursor-pointer gap-5 px-4 py-5 outline-none transition-all duration-300 hover:bg-white/[0.035] focus-visible:bg-white/[0.045] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#5260FF]/45 sm:px-5 lg:grid-cols-[minmax(220px,1.05fr)_132px_minmax(300px,1.55fr)_140px_135px_82px] lg:items-center lg:gap-4"
      >
        <span className="absolute bottom-3 left-0 top-3 w-[3px] rounded-full opacity-80" style={{ backgroundColor: brandColour }} />
        <div className="flex min-w-0 items-center gap-3.5">
          <span className="relative">
            <Avatar name={team.name} imageUrl={team.prefs?.logo_url} size="lg" square contain />
            <span className="absolute -bottom-1 -right-1 h-3.5 w-3.5 rounded-full border-[3px] border-[#111319]" style={{ backgroundColor: brandColour }} />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="truncate text-[15px] font-extrabold tracking-[-0.02em] text-white/95">{team.name}</h3>
              {parseBool(team.prefs?.partner) ? <Icon icon="solar:star-bold" className="h-3.5 w-3.5 shrink-0 text-amber-300" /> : null}
            </div>
            <p className="mt-1 truncate font-mono text-[9px] uppercase tracking-[0.08em] text-white/35">{isStoredValue(team.prefs?.lvl1_id) ? `#${team.prefs.lvl1_id}` : team.$id}</p>
            <p className="mt-1 truncate text-[10px] font-medium text-white/48">{companyMeta || 'Location not set'}</p>
          </div>
        </div>
        <div onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}><AccessStatusSelect value={status} onChange={onStatusChange} busy={statusBusy} label={`${team.name} status`} /></div>
        <ModuleStrip team={team} />
        <MemberStack members={members} />
        <div className="min-w-0">
          <div className="flex items-baseline gap-1.5">{metricsLoading && !metrics ? <Icon icon="bx:loader-circle" className="h-4 w-4 animate-spin text-white/40" /> : <span className="text-lg font-black tracking-[-0.04em] text-white/90">{typeof metrics?.credits === 'number' ? metrics.credits.toLocaleString() : '—'}</span>}<span className="text-[9px] font-black uppercase tracking-[0.14em] text-white/34">credits</span></div>
          <div className="mt-2"><StatusChips counts={campaigns} /></div>
        </div>
        <div className="flex items-center justify-end gap-1.5" onClick={(event) => event.stopPropagation()}>
          <button type="button" onClick={onOpenDetails} className="flex h-9 items-center gap-1.5 rounded-xl border border-white/[0.12] bg-white/[0.045] px-2.5 text-[8px] font-black uppercase tracking-[0.1em] text-white/55 transition-all hover:border-[#6673ff]/35 hover:bg-[#5260FF]/12 hover:text-[#aeb5ff]" aria-label={`Open ${team.name} profile`} title="Open team profile"><Icon icon="solar:user-id-linear" className="h-3.5 w-3.5" /><span className="hidden xl:inline">Profile</span></button>
          <button type="button" onClick={onToggle} aria-expanded={isExpanded} aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${team.name} people`} className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/[0.1] bg-white/[0.035] text-white/45 transition-colors hover:border-white/20 hover:text-white/75"><Icon icon="bx:chevron-down" className={`h-4 w-4 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} /></button>
        </div>
      </article>

      {isExpanded ? (
        <div className="animate-fadeIn border-t border-white/[0.1] bg-[#0c0e13] px-3 py-3 sm:px-4 sm:py-4">
          <div className="mb-3 flex items-center justify-between gap-3 px-1"><div><p className="text-[10px] font-black uppercase tracking-[0.14em] text-white/65">People in {team.name}</p><p className="mt-1 text-[9px] text-white/35">Open, impersonate, or update a person's status here.</p></div><span className="rounded-full border border-white/[0.1] bg-white/[0.045] px-2.5 py-1 text-[9px] font-bold text-white/50">{members.length} total</span></div>
          <div className="space-y-2">
            {members.length ? members.map((member) => {
              const isInvitation = member.is_invitation || member.membership_status === 'pending';
              const memberStatus = userStatusOverrides[member.$id] || statusOfUser(member);
              const memberRole = member.labels?.includes('superadmin') ? 'Superadmin' : member.labels?.includes('teamadmin') ? 'Team admin' : 'Member';
              return (
                <div key={member.membership_id || member.$id} className="grid gap-3 rounded-[16px] border border-white/[0.095] bg-[#15171d] p-3 transition-colors hover:border-white/[0.15] sm:grid-cols-[minmax(0,1fr)_120px_132px_minmax(148px,auto)] sm:items-center">
                  <button type="button" disabled={isInvitation} onClick={() => !isInvitation && onOpenUser(member.$id)} className={`flex min-w-0 items-center gap-3 text-left ${isInvitation ? 'cursor-default' : 'group/user'}`}>
                    <Avatar name={member.name || member.email} imageUrl={member.prefs?.profilePictureUrl} />
                    <span className="min-w-0"><span className="flex items-center gap-1.5"><b className="truncate text-[12px] text-white/82 group-hover/user:text-white">{member.name || 'Unnamed person'}</b>{member.emailVerification ? <Icon icon="solar:verified-check-bold" className="h-3 w-3 shrink-0 text-sky-300" /> : null}</span><span className="mt-1 block truncate text-[10px] text-white/40">{member.email}</span></span>
                  </button>
                  <span className="w-fit rounded-lg border border-white/[0.1] bg-white/[0.04] px-2 py-1 text-[8px] font-black uppercase tracking-[0.1em] text-white/48">{memberRole}</span>
                  <div onClick={(event) => event.stopPropagation()}><AccessStatusSelect value={memberStatus} onChange={(next) => onUserStatusChange(member, next)} disabled={isInvitation} busy={pendingStatusKey === `user:${member.$id}`} label={`${member.name || member.email} status`} /></div>
                  <div className="flex items-center justify-end gap-1.5">
                    {isInvitation ? <Icon icon="solar:letter-unread-linear" className="h-4 w-4 text-sky-300/55" /> : (
                      <>
                        {onImpersonate ? <ImpersonateButton userName={member.name || member.email} onClick={() => onImpersonate(member)} /> : null}
                        <button type="button" onClick={() => onOpenUser(member.$id)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/[0.1] bg-white/[0.04] text-white/40 transition-colors hover:border-[#6673ff]/35 hover:text-[#aeb5ff]" aria-label={`Open ${member.name || member.email}`}><Icon icon="solar:arrow-right-up-linear" className="h-4 w-4" /></button>
                      </>
                    )}
                  </div>
                </div>
              );
            }) : <div className="rounded-[16px] border border-dashed border-white/[0.1] py-8 text-center text-[10px] text-white/35">No people are connected to this team yet.</div>}
          </div>
        </div>
      ) : null}
    </section>
  );
};

interface UserDirectoryRowProps {
  user: AdminUser;
  team?: AdminTeam;
  status: AdminAccessStatus;
  statusBusy: boolean;
  onOpen: () => void;
  onStatusChange: (status: AdminAccessStatus) => void;
  onImpersonate?: () => void;
}

const UserDirectoryRow: React.FC<UserDirectoryRowProps> = ({ user, team, status, statusBusy, onOpen, onStatusChange, onImpersonate }) => {
  const isInvitation = user.is_invitation || user.membership_status === 'pending';
  const primaryRole = user.labels?.includes('superadmin') ? 'Superadmin' : user.labels?.includes('teamadmin') ? 'Team admin' : 'Member';

  return (
    <article
      onClick={isInvitation ? undefined : onOpen}
      className={`group grid gap-4 rounded-[20px] border border-white/[0.11] bg-[#111319] px-4 py-4 shadow-[0_14px_42px_rgba(0,0,0,0.22)] outline-none transition-all sm:px-5 lg:grid-cols-[minmax(260px,1.45fr)_minmax(170px,1fr)_130px_132px_125px_98px] lg:items-center ${isInvitation ? 'cursor-default' : 'cursor-pointer hover:border-white/[0.17] hover:bg-[#15171d] focus-visible:ring-2 focus-visible:ring-[#5260FF]/45'}`}
    >
      <div className="flex min-w-0 items-center gap-3.5">
        <span className="relative">
          <Avatar name={user.name || user.email} imageUrl={user.prefs?.profilePictureUrl} />
          <span className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-[3px] border-[#111319] ${ADMIN_ACCESS_STATUS[status].dot}`} />
        </span>
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-2">
            <h3 className="truncate text-[14px] font-extrabold tracking-[-0.015em] text-white/90">{user.name || 'Unnamed person'}</h3>
            {user.emailVerification ? <Icon icon="solar:verified-check-bold" className="h-3.5 w-3.5 shrink-0 text-sky-300" /> : null}
          </div>
          <p className="mt-0.5 truncate text-[11px] text-white/38">{user.email}</p>
          {isStoredValue(user.prefs?.job_title) ? <p className="mt-1 truncate text-[10px] text-white/28">{user.prefs.job_title}</p> : null}
        </div>
      </div>
      <div className="flex min-w-0 items-center gap-2.5">
        <Avatar name={team?.name || 'No team'} imageUrl={team?.prefs?.logo_url} size="sm" square contain />
        <div className="min-w-0">
          <p className="truncate text-[11px] font-bold text-white/68">{team?.name || 'No team assigned'}</p>
          <p className="mt-0.5 truncate text-[9px] uppercase tracking-[0.1em] text-white/24">{team?.prefs?.country_code || 'Workspace'}</p>
        </div>
      </div>
      <div>
        <span className={`inline-flex rounded-lg border px-2 py-1 text-[9px] font-black uppercase tracking-[0.1em] ${
          primaryRole === 'Superadmin'
            ? 'border-rose-400/20 bg-rose-400/[0.08] text-rose-200'
            : primaryRole === 'Team admin'
              ? 'border-[#6875FF]/25 bg-[#5260FF]/10 text-[#aeb6ff]'
              : 'border-white/[0.07] bg-white/[0.025] text-white/42'
        }`}>{primaryRole}</span>
      </div>
      <div onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
        <AccessStatusSelect value={status} onChange={onStatusChange} disabled={isInvitation} busy={statusBusy} label={`${user.name || user.email} status`} />
      </div>
      <div>
        <p className="text-[10px] font-bold text-white/58">{isInvitation ? 'Invitation sent' : user.emailVerification ? 'Email verified' : 'Not verified'}</p>
        <p className="mt-1 text-[9px] text-white/24">{formatDate(isInvitation ? user.invited_at : user.$createdAt)}</p>
      </div>
      <div className="flex items-center justify-end gap-1.5" onClick={(event) => event.stopPropagation()}>
        {!isInvitation && onImpersonate ? (
          <ImpersonateButton userName={user.name || user.email} onClick={onImpersonate} />
        ) : null}
        {!isInvitation ? (
          <button type="button" onClick={onOpen} className="flex h-9 w-9 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.025] text-white/35 transition-all hover:border-white/15 hover:text-white/80" aria-label={`Open ${user.name || user.email}`}>
            <Icon icon="solar:arrow-right-up-linear" className="h-4 w-4" />
          </button>
        ) : <Icon icon="solar:letter-unread-linear" className="mr-2 h-4 w-4 text-sky-300/55" />}
      </div>
    </article>
  );
};

const SnapshotCard: React.FC<{ eyebrow: string; value: string; detail: string; icon: string; tone?: 'default' | 'attention' }> = ({ eyebrow, value, detail, icon, tone = 'default' }) => (
  <div className="group relative overflow-hidden rounded-[22px] border border-white/[0.12] bg-[#111319] p-4 shadow-[0_18px_60px_rgba(0,0,0,0.28)] transition-all duration-300 hover:-translate-y-0.5 hover:border-white/[0.19] sm:p-5">
    <span className={`absolute -right-5 -top-5 h-20 w-20 rounded-full blur-3xl transition-opacity group-hover:opacity-100 ${tone === 'attention' ? 'bg-amber-400/10 opacity-70' : 'bg-[#5260FF]/12 opacity-50'}`} />
    <div className="relative flex items-start justify-between gap-3">
      <div>
        <p className="text-[9px] font-black uppercase tracking-[0.19em] text-white/28">{eyebrow}</p>
        <p className="mt-3 text-[30px] font-black leading-none tracking-[-0.055em] text-white/92">{value}</p>
        <p className="mt-2 text-[10px] font-medium text-white/32">{detail}</p>
      </div>
      <span className={`flex h-9 w-9 items-center justify-center rounded-xl border ${tone === 'attention' ? 'border-amber-300/18 bg-amber-300/[0.07] text-amber-200' : 'border-[#6875FF]/18 bg-[#5260FF]/[0.08] text-[#8490ff]'}`}>
        <Icon icon={icon} className="h-4 w-4" />
      </span>
    </div>
  </div>
);


/** Admin → Reports: LEADS | CAMPAIGNS sub-tabs. LEADS = section-config editor;
 *  CAMPAIGNS = the installed campaign template inventory + live previews. */
function ReportsSection() {
  const [category, setCategory] = useState<'leads' | 'campaigns' | 'sellers'>('leads');
  return <div>
    <nav aria-label="Report categories" className="mb-6 inline-flex rounded-2xl border border-white/[0.09] bg-black/30 p-1">
      {([
        { key: 'leads' as const, label: 'Leads', icon: 'solar:user-rounded-linear' },
        { key: 'campaigns' as const, label: 'Campaigns', icon: 'solar:flag-linear' },
        { key: 'sellers' as const, label: 'Sellers', icon: 'solar:buildings-2-linear' },
      ]).map((item) => (
        <button
          key={item.key}
          type="button"
          onClick={() => setCategory(item.key)}
          aria-current={category === item.key ? 'page' : undefined}
          className={`inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-[10px] font-black uppercase tracking-[0.13em] transition-all ${
            category === item.key ? 'bg-white/[0.09] text-white shadow-[0_4px_18px_rgba(0,0,0,0.22)]' : 'text-white/32 hover:text-white/60'
          }`}
        >
          <Icon icon={item.icon} className="h-4 w-4" />
          {item.label}
        </button>
      ))}
    </nav>
    {category === 'leads' ? <ReportTemplateEditor /> : category === 'sellers' ? <SellerReportTemplate /> : <CampaignTemplateEditor />}
  </div>;
}


export const AdminDashboard: React.FC<AdminDashboardProps> = ({ onClientSelect }) => {
  const { data, isLoading, isFetching, error } = useAdminOverview();
  // Top-level admin sections — keeps account management and platform
  // configuration from mixing on one page.
  const [adminTab, setAdminTab] = useState<'accounts' | 'reports'>('accounts');
  const [directoryView, setDirectoryView] = useState<DirectoryView>('teams');
  const [searchTerm, setSearchTerm] = useState('');
  const [includeInternal, setIncludeInternal] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [moduleFilter, setModuleFilter] = useState<'all' | ModuleField>('all');
  const [showInsights, setShowInsights] = useState(false);
  const [editingTeam, setEditingTeam] = useState<AdminTeam | null>(null);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [pendingStatusKey, setPendingStatusKey] = useState<string | null>(null);
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(() => new Set());
  const [teamStatusOverrides, setTeamStatusOverrides] = useState<Record<string, AdminAccessStatus>>({});
  const [userStatusOverrides, setUserStatusOverrides] = useState<Record<string, AdminAccessStatus>>({});

  const updateTeamPrefs = useUpdateTeamPrefs();
  const updateUserPrefs = useUpdateUserPrefs();
  const teams = data?.teams || [];
  const users = data?.users || [];
  const { metrics: teamMetrics, loading: metricsLoading } = useAdminTeamMetrics(teams);
  const isFirstLoad = isLoading && !data;

  const { data: countries = [] } = useQuery({ queryKey: ['geo', 'countries'], queryFn: () => geoService.listCountries(), staleTime: Infinity, retry: 1 });
  const countryNames = useMemo(() => new Map(countries.map((country) => [country.iso2, country.name])), [countries]);
  const teamById = useMemo(() => new Map(teams.map((team) => [team.$id, team])), [teams]);
  const usersById = useMemo(() => new Map(users.map((user) => [user.$id, user])), [users]);
  const usersByMembershipId = useMemo(() => new Map(users.filter((user) => user.membership_id).map((user) => [user.membership_id as string, user])), [users]);
  const membersByTeamId = useMemo(() => {
    const result = new Map<string, AdminUser[]>();
    teams.forEach((team) => {
      const roster = team.memberships || team.members || [];
      const members = roster.flatMap((membership) => {
        const match = usersByMembershipId.get(membership.membership_id) || (membership.user_id ? usersById.get(membership.user_id) : undefined);
        if (!match) return [];
        return [{
          ...match,
          team_id: team.$id,
          membership_id: membership.membership_id,
          membership_status: membership.status,
          invited_at: membership.invited_at,
          is_invitation: membership.status === 'pending',
        }];
      });
      result.set(team.$id, members);
    });
    return result;
  }, [teams, usersById, usersByMembershipId]);

  const scopedTeams = useMemo(() => teams.filter((team) => includeInternal || !isInternalTeam(team)), [teams, includeInternal]);
  const scopedTeamIds = useMemo(() => new Set(scopedTeams.map((team) => team.$id)), [scopedTeams]);
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const visibleTeams = useMemo(() => scopedTeams.filter((team) => {
    const status = teamStatusOverrides[team.$id] || statusOfTeam(team);
    if (statusFilter !== 'all' && status !== statusFilter) return false;
    if (moduleFilter !== 'all') {
      const module = MODULE_CATALOG.find((entry) => entry.field === moduleFilter);
      if (!module || !parseBool(team.prefs?.[module.prefKey])) return false;
    }
    if (!normalizedSearch) return true;
    const members = membersByTeamId.get(team.$id) || [];
    const haystack = [team.name, team.$id, team.prefs?.lvl1_id, team.prefs?.billing_email, team.prefs?.company_website, ...members.flatMap((member) => [member.name, member.email])].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(normalizedSearch);
  }), [membersByTeamId, moduleFilter, normalizedSearch, scopedTeams, statusFilter, teamStatusOverrides]);
  const visibleUsers = useMemo(() => users.filter((user) => {
    if (user.team_id && !scopedTeamIds.has(user.team_id)) return false;
    const status = userStatusOverrides[user.$id] || statusOfUser(user);
    if (statusFilter !== 'all' && status !== statusFilter) return false;
    if (!normalizedSearch) return true;
    const team = user.team_id ? teamById.get(user.team_id) : undefined;
    return [user.name, user.email, user.prefs?.job_title, team?.name, ...(user.labels || [])].filter(Boolean).join(' ').toLowerCase().includes(normalizedSearch);
  }), [normalizedSearch, scopedTeamIds, statusFilter, teamById, userStatusOverrides, users]);

  const snapshot = useMemo(() => {
    const scopedUsers = users.filter((user) => !user.team_id || scopedTeamIds.has(user.team_id));
    const invited = scopedUsers.filter((user) => statusOfUser(user) === 'invited').length;
    const modules = scopedTeams.reduce((total, team) => total + MODULE_CATALOG.filter((module) => parseBool(team.prefs?.[module.prefKey])).length, 0);
    const attention = scopedTeams.filter((team) => statusOfTeam(team) !== 'active').length + scopedUsers.filter((user) => ['blocked', 'inactive'].includes(statusOfUser(user))).length;
    const credits = scopedTeams.reduce((total, team) => {
      const value = teamMetrics[team.$id]?.credits;
      return total + (typeof value === 'number' ? value : 0);
    }, 0);
    return { people: scopedUsers.length, invited, modules, attention, credits };
  }, [scopedTeamIds, scopedTeams, teamMetrics, users]);
  const chartData: BarDatum[] = useMemo(() => scopedTeams
    .map((team) => ({ team, metrics: teamMetrics[team.$id] }))
    .filter(({ metrics }) => typeof metrics?.credits === 'number' && metrics.credits > 0)
    .sort((a, b) => (b.metrics?.credits || 0) - (a.metrics?.credits || 0))
    .slice(0, 10)
    .map(({ team, metrics }) => ({ id: team.$id, label: team.name, value: metrics?.credits || 0, detail: <StatusChips counts={metrics?.campaigns_by_status} /> })), [scopedTeams, teamMetrics]);

  const confirmRestrictiveChange = (subject: string, next: AdminAccessStatus): boolean => {
    if (next !== 'blocked' && next !== 'inactive') return true;
    return window.confirm(`Change ${subject} to ${ADMIN_ACCESS_STATUS[next].label}? This is an access-sensitive admin change.`);
  };
  const handleTeamStatusChange = async (team: AdminTeam, next: AdminAccessStatus) => {
    const current = teamStatusOverrides[team.$id] || statusOfTeam(team);
    if (current === next || !confirmRestrictiveChange(team.name, next)) return;
    const pendingKey = `team:${team.$id}`;
    setTeamStatusOverrides((previous) => ({ ...previous, [team.$id]: next }));
    setPendingStatusKey(pendingKey);
    try {
      await updateTeamPrefs.mutateAsync({ teamId: team.$id, prefs: { status: next } });
      toast.success(`${team.name} is now ${ADMIN_ACCESS_STATUS[next].label.toLowerCase()}`);
    } catch (statusError) {
      setTeamStatusOverrides((previous) => { const { [team.$id]: _removed, ...rest } = previous; return rest; });
      toast.error((statusError as Error).message || 'Could not update team status');
    } finally { setPendingStatusKey(null); }
  };
  const handleUserStatusChange = async (user: AdminUser, next: AdminAccessStatus) => {
    if (user.is_invitation) return;
    const current = userStatusOverrides[user.$id] || statusOfUser(user);
    if (current === next || !confirmRestrictiveChange(user.name || user.email, next)) return;
    const pendingKey = `user:${user.$id}`;
    setUserStatusOverrides((previous) => ({ ...previous, [user.$id]: next }));
    setPendingStatusKey(pendingKey);
    try {
      await updateUserPrefs.mutateAsync({ userId: user.$id, prefs: { status: next } });
      toast.success(`${user.name || user.email} is now ${ADMIN_ACCESS_STATUS[next].label.toLowerCase()}`);
    } catch (statusError) {
      setUserStatusOverrides((previous) => { const { [user.$id]: _removed, ...rest } = previous; return rest; });
      toast.error((statusError as Error).message || 'Could not update user status');
    } finally { setPendingStatusKey(null); }
  };
  const toggleTeamExpanded = (teamId: string) => {
    setExpandedTeams((previous) => {
      const next = new Set(previous);
      if (next.has(teamId)) next.delete(teamId);
      else next.add(teamId);
      return next;
    });
  };

  const internalCount = teams.filter(isInternalTeam).length;
  const resultCount = directoryView === 'teams' ? visibleTeams.length : visibleUsers.length;

  return (
    <div className="relative mx-auto w-full max-w-[1480px] overflow-hidden px-4 pb-16 pt-8 sm:px-6 sm:pt-11 lg:px-9">
      <div className="pointer-events-none absolute -right-40 -top-56 h-[560px] w-[560px] rounded-full bg-[#5260FF]/[0.075] blur-[140px]" />
      <div className="pointer-events-none absolute left-[28%] top-0 h-px w-[42%] bg-gradient-to-r from-transparent via-[#8791ff]/40 to-transparent" />
      <header className="relative mb-8 grid gap-7 lg:grid-cols-[1fr_auto] lg:items-end">
        <div>
          <div className="mb-4 flex items-center gap-3">
            <span className="font-mono text-[9px] font-bold uppercase tracking-[0.24em] text-[#8791ff]">{adminTab === 'reports' ? 'Admin / Reports' : 'Admin / Directory'}</span>
            <span className="h-px w-12 bg-[#5260FF]/35" />
            <span className="inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.14em] text-white/28">
              <span className={`h-1.5 w-1.5 rounded-full ${isFetching ? 'animate-pulse bg-amber-300' : 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.7)]'}`} />
              {isFetching ? 'Syncing' : 'Live'}
            </span>
          </div>
          <h1 className="max-w-4xl text-[clamp(2.45rem,5.5vw,5.6rem)] font-black leading-[0.88] tracking-[-0.075em] text-white">
            {adminTab === 'reports' ? <>Templates<br /><span className="bg-gradient-to-r from-white via-white/80 to-white/35 bg-clip-text text-transparent"></span></> : <>Admin Panel<br /><span className="bg-gradient-to-r from-white via-white/80 to-white/35 bg-clip-text text-transparent"></span></>}
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-6 text-white/40 sm:text-[15px]">{adminTab === 'reports' ? 'Choose which sections each report template includes, and in what order.' : 'See access, modules and people in one scan. Open any record for the complete operational picture.'}</p>
        </div>
        {adminTab === 'accounts' && (
          <button type="button" onClick={() => setShowInsights((value) => !value)} className="group inline-flex h-11 items-center justify-center gap-2 rounded-full border border-white/[0.09] bg-white/[0.025] px-4 text-[10px] font-black uppercase tracking-[0.14em] text-white/48 transition-all hover:border-white/15 hover:bg-white/[0.045] hover:text-white/80">
            <Icon icon="solar:chart-2-linear" className="h-4 w-4 text-[#8791ff]" />Portfolio insights
            <Icon icon="bx:chevron-down" className={`h-4 w-4 transition-transform ${showInsights ? 'rotate-180' : ''}`} />
          </button>
        )}
      </header>

      {/* Top navigation — one concern per tab */}
      <nav aria-label="Admin sections" className="mb-6 inline-flex rounded-2xl border border-white/[0.09] bg-black/30 p-1">
        {([
          { key: 'accounts' as const, label: 'Accounts', icon: 'solar:users-group-rounded-linear' },
          { key: 'reports' as const, label: 'Reports', icon: 'solar:document-text-linear' },
        ]).map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setAdminTab(item.key)}
            aria-current={adminTab === item.key ? 'page' : undefined}
            className={`inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-[10px] font-black uppercase tracking-[0.13em] transition-all ${
              adminTab === item.key ? 'bg-white/[0.09] text-white shadow-[0_4px_18px_rgba(0,0,0,0.22)]' : 'text-white/32 hover:text-white/60'
            }`}
          >
            <Icon icon={item.icon} className="h-4 w-4" />
            {item.label}
          </button>
        ))}
      </nav>

      {adminTab === 'reports' ? (
        <ReportsSection />
      ) : isFirstLoad ? <BrainiacsLoader variant="section" size="md" message="Building the client directory…" /> : error ? (
        <div className="rounded-[28px] border border-rose-400/20 bg-rose-400/[0.07] p-10 text-center">
          <Icon icon="solar:danger-triangle-linear" className="mx-auto h-7 w-7 text-rose-300" />
          <p className="mt-3 text-sm font-black text-rose-200">The directory could not sync</p><p className="mt-1 text-xs text-white/45">{(error as Error).message}</p>
        </div>
      ) : (
        <div className="relative space-y-6">
          <section aria-label="Workspace snapshot" className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
            <SnapshotCard eyebrow="Client teams" value={scopedTeams.length.toLocaleString()} detail={`${internalCount} internal ${includeInternal ? 'included' : 'hidden'}`} icon="solar:buildings-2-linear" />
            <SnapshotCard eyebrow="People" value={snapshot.people.toLocaleString()} detail={`${snapshot.invited} awaiting activation`} icon="solar:users-group-rounded-linear" />
            <SnapshotCard eyebrow="Modules live" value={snapshot.modules.toLocaleString()} detail={`Across ${scopedTeams.length} workspaces`} icon="solar:widget-5-linear" />
            <SnapshotCard eyebrow="Needs attention" value={snapshot.attention.toLocaleString()} detail="Restricted or inactive records" icon="solar:shield-warning-linear" tone="attention" />
          </section>
          {showInsights ? (
            <section className="animate-fadeIn rounded-[28px] border border-white/[0.065] bg-[#0b0c0f]/92 p-5 shadow-[0_24px_80px_rgba(0,0,0,0.25)] sm:p-6">
              <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
                <div><p className="font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-[#8791ff]">Portfolio signal</p><h2 className="mt-1.5 text-lg font-black tracking-[-0.025em] text-white/90">Credit distribution</h2><p className="mt-1 text-[11px] text-white/32">Last-known cached balances; unknown values stay blank.</p></div>
                <p className="text-right text-2xl font-black tracking-[-0.045em] text-white/85">{snapshot.credits.toLocaleString()} <span className="text-[9px] uppercase tracking-[0.15em] text-white/24">known credits</span></p>
              </div>
              <CreditsBarChart data={chartData} emptyLabel="No cached credit balances are available" />
            </section>
          ) : null}

          <section className="overflow-hidden rounded-[30px] border border-white/[0.12] bg-[#0e1015]/95 shadow-[0_30px_100px_rgba(0,0,0,0.38)]">
            <div className="border-b border-white/[0.10] bg-white/[0.018] px-4 pb-4 pt-4 sm:px-5 sm:pt-5">
              <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                <div className="inline-flex w-full rounded-2xl border border-white/[0.11] bg-black/30 p-1 sm:w-auto">
                  {([{ key: 'teams' as const, label: 'Teams', count: scopedTeams.length, icon: 'solar:buildings-2-linear' }, { key: 'people' as const, label: 'People', count: users.filter((user) => !user.team_id || scopedTeamIds.has(user.team_id)).length, icon: 'solar:users-group-rounded-linear' }]).map((item) => (
                    <button key={item.key} type="button" onClick={() => { setDirectoryView(item.key); setModuleFilter('all'); }} className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-[10px] font-black uppercase tracking-[0.13em] transition-all sm:flex-none ${directoryView === item.key ? 'bg-white/[0.09] text-white shadow-[0_4px_18px_rgba(0,0,0,0.22)]' : 'text-white/32 hover:text-white/60'}`}>
                      <Icon icon={item.icon} className="h-4 w-4" />{item.label}<span className={`rounded-md px-1.5 py-0.5 font-mono text-[8px] ${directoryView === item.key ? 'bg-[#5260FF] text-white' : 'bg-white/[0.05] text-white/35'}`}>{item.count}</span>
                    </button>
                  ))}
                </div>
                <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2">
                  <label className="group relative min-w-[220px] flex-1 xl:max-w-[360px]"><span className="sr-only">Search the admin directory</span><Icon icon="solar:magnifier-linear" className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/25 transition-colors group-focus-within:text-[#8791ff]" /><input type="search" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder={directoryView === 'teams' ? 'Search teams or members…' : 'Search people, teams or roles…'} className="h-11 w-full rounded-xl border border-white/[0.075] bg-white/[0.025] pl-10 pr-4 text-xs font-medium text-white outline-none placeholder:text-white/20 focus:border-[#5260FF]/40 focus:bg-white/[0.04]" /></label>
                  <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as StatusFilter)} aria-label="Filter by status" className="h-11 rounded-xl border border-white/[0.075] bg-[#101116] px-3 text-[10px] font-black uppercase tracking-[0.11em] text-white/50 outline-none hover:border-white/15 focus:border-[#5260FF]/40"><option value="all">All statuses</option>{(Object.keys(ADMIN_ACCESS_STATUS) as AdminAccessStatus[]).map((status) => <option key={status} value={status}>{ADMIN_ACCESS_STATUS[status].label}</option>)}</select>
                  {directoryView === 'teams' ? <select value={moduleFilter} onChange={(event) => setModuleFilter(event.target.value as 'all' | ModuleField)} aria-label="Filter by module" className="h-11 rounded-xl border border-white/[0.075] bg-[#101116] px-3 text-[10px] font-black uppercase tracking-[0.11em] text-white/50 outline-none hover:border-white/15 focus:border-[#5260FF]/40"><option value="all">All modules</option>{MODULE_CATALOG.map((module) => <option key={module.field} value={module.field}>{module.label}</option>)}</select> : null}
                  {internalCount > 0 ? <label className="flex h-11 cursor-pointer items-center gap-2.5 rounded-xl border border-white/[0.075] bg-white/[0.025] px-3 text-[9px] font-black uppercase tracking-[0.1em] text-white/38 transition-colors hover:border-white/15 hover:text-white/60"><ToggleSwitch checked={includeInternal} onChange={setIncludeInternal} compact />Internal</label> : null}
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between gap-3 text-[10px] text-white/34"><p><b className="text-white/68">{resultCount}</b> result{resultCount === 1 ? '' : 's'}</p>{(searchTerm || statusFilter !== 'all' || moduleFilter !== 'all') ? <button type="button" onClick={() => { setSearchTerm(''); setStatusFilter('all'); setModuleFilter('all'); }} className="font-bold text-[#8791ff]/75 transition-colors hover:text-[#9da5ff]">Clear filters</button> : <p>{directoryView === 'teams' ? 'Click a team to show its people' : 'Click a person for the full record'}</p>}</div>
            </div>

            {directoryView === 'teams' ? (
              <div><div className="hidden grid-cols-[minmax(220px,1.05fr)_132px_minmax(300px,1.55fr)_140px_135px_82px] gap-4 border-b border-white/[0.08] bg-black/15 px-5 py-3 font-mono text-[8px] font-bold uppercase tracking-[0.18em] text-white/30 lg:grid"><span>Team</span><span>Status</span><span>Modules</span><span>People</span><span>Signal</span><span>Open</span></div>
                <div className="space-y-3 p-3 sm:p-4">
                  {visibleTeams.map((team) => {
                    const countryCode = isStoredValue(team.prefs?.country_code) ? team.prefs.country_code : '';
                    return (
                      <TeamDirectoryRow
                        key={team.$id}
                        team={team}
                        members={membersByTeamId.get(team.$id) || []}
                        metrics={teamMetrics[team.$id]}
                        metricsLoading={metricsLoading}
                        status={teamStatusOverrides[team.$id] || statusOfTeam(team)}
                        statusBusy={pendingStatusKey === `team:${team.$id}`}
                        countryLabel={countryCode ? countryNames.get(countryCode) || countryCode : ''}
                        isExpanded={expandedTeams.has(team.$id)}
                        userStatusOverrides={userStatusOverrides}
                        pendingStatusKey={pendingStatusKey}
                        onToggle={() => toggleTeamExpanded(team.$id)}
                        onOpenDetails={() => setEditingTeam(team)}
                        onOpenUser={setEditingUserId}
                        onImpersonate={onClientSelect ? (user) => onClientSelect(user.email) : undefined}
                        onStatusChange={(status) => void handleTeamStatusChange(team, status)}
                        onUserStatusChange={(user, status) => void handleUserStatusChange(user, status)}
                      />
                    );
                  })}
                </div>
              </div>
            ) : (
              <div><div className="hidden grid-cols-[minmax(260px,1.45fr)_minmax(170px,1fr)_130px_132px_125px_98px] gap-4 border-b border-white/[0.055] bg-white/[0.012] px-5 py-3 font-mono text-[8px] font-bold uppercase tracking-[0.18em] text-white/22 lg:grid"><span>Person</span><span>Team</span><span>Role</span><span>Status</span><span>Account</span><span /></div>
                <div className="space-y-2.5 p-3 sm:p-4">{visibleUsers.map((user) => <UserDirectoryRow key={user.$id} user={user} team={user.team_id ? teamById.get(user.team_id) : undefined} status={userStatusOverrides[user.$id] || statusOfUser(user)} statusBusy={pendingStatusKey === `user:${user.$id}`} onOpen={() => setEditingUserId(user.$id)} onStatusChange={(status) => void handleUserStatusChange(user, status)} onImpersonate={onClientSelect && !user.is_invitation ? () => onClientSelect(user.email) : undefined} />)}</div>
              </div>
            )}
            {resultCount === 0 ? <div className="flex min-h-64 flex-col items-center justify-center px-6 text-center"><span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/[0.07] bg-white/[0.025] text-white/25"><Icon icon="solar:magnifier-linear" className="h-5 w-5" /></span><p className="mt-4 text-sm font-black text-white/60">Nothing matches this view</p><p className="mt-1 max-w-sm text-[11px] leading-5 text-white/28">Clear a filter or try a team, person, email or workspace ID.</p></div> : null}
          </section>
        </div>
      )}

      <TeamEditorDrawer team={editingTeam} onClose={() => setEditingTeam(null)} onUserSelect={(userId) => { setEditingTeam(null); setEditingUserId(userId); }} />
      <UserEditorDrawer userId={editingUserId} onClose={() => setEditingUserId(null)} onImpersonate={onClientSelect} />
    </div>
  );
};
